import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid2'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import {
  TASK_PRIORITIES,
  TASK_PRIORITY_LABELS,
  TASK_STATUSES,
  TASK_STATUS_LABELS,
  type TaskPriority,
  type TaskStatus,
} from '@lawyer/shared'
import { useCaseOptions, useClientOptions, useProjectOptions, useUsers } from '../../hooks/useLookups'

export type TaskFilterState = {
  search: string
  assignedUserId: string
  clientId: string
  caseId: string
  projectId: string
  status: TaskStatus[]
  priority: TaskPriority[]
  topLevelOnly: boolean
}

export const EMPTY_FILTERS: TaskFilterState = {
  search: '',
  assignedUserId: '',
  clientId: '',
  caseId: '',
  projectId: '',
  status: [],
  priority: [],
  topLevelOnly: false,
}

export const hasActiveFilters = (filters: TaskFilterState): boolean =>
  Boolean(
    filters.search ||
      filters.assignedUserId ||
      filters.clientId ||
      filters.caseId ||
      filters.projectId ||
      filters.status.length ||
      filters.priority.length ||
      filters.topLevelOnly
  )

type Props = {
  value: TaskFilterState
  onChange: (next: TaskFilterState) => void
  /** Filters that are fixed by the context the table is embedded in. */
  locked?: Array<keyof TaskFilterState>
}

/**
 * Server-side filter bar. The DataGrid community edition only supports one built-in
 * filter at a time, so filtering is driven from here through query params instead.
 */
export function TaskFilters({ value, onChange, locked = [] }: Props) {
  const users = useUsers()
  const clients = useClientOptions()
  const cases = useCaseOptions()
  const projects = useProjectOptions()

  const set = <K extends keyof TaskFilterState>(key: K, next: TaskFilterState[K]) =>
    onChange({ ...value, [key]: next })

  const isLocked = (key: keyof TaskFilterState) => locked.includes(key)

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Grid container spacing={1.5} alignItems="center">
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            label="חיפוש"
            value={value.search}
            onChange={(e) => set('search', e.target.value)}
            placeholder="שם או תיאור"
          />
        </Grid>

        {!isLocked('assignedUserId') ? (
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              select
              label="עובד"
              value={value.assignedUserId}
              onChange={(e) => set('assignedUserId', e.target.value)}
            >
              <MenuItem value="">הכל</MenuItem>
              {(users.data ?? []).map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        ) : null}

        {!isLocked('clientId') ? (
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              select
              label="לקוח"
              value={value.clientId}
              onChange={(e) => set('clientId', e.target.value)}
            >
              <MenuItem value="">הכל</MenuItem>
              {(clients.data ?? []).map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.displayName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        ) : null}

        {!isLocked('caseId') ? (
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              select
              label="תיק"
              value={value.caseId}
              onChange={(e) => set('caseId', e.target.value)}
            >
              <MenuItem value="">הכל</MenuItem>
              {(cases.data ?? []).map((row) => (
                <MenuItem key={row.id} value={row.id}>
                  {row.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        ) : null}

        {!isLocked('projectId') ? (
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              select
              label="פרויקט"
              value={value.projectId}
              onChange={(e) => set('projectId', e.target.value)}
            >
              <MenuItem value="">הכל</MenuItem>
              {(projects.data ?? []).map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.displayName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        ) : null}

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            select
            label="סטטוס"
            value={value.status}
            SelectProps={{
              multiple: true,
              renderValue: (selected) =>
                (selected as TaskStatus[]).map((s) => TASK_STATUS_LABELS[s]).join(', ') || 'הכל',
            }}
            onChange={(e) =>
              set('status', (typeof e.target.value === 'string' ? [] : e.target.value) as TaskStatus[])
            }
          >
            {TASK_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                <Checkbox checked={value.status.includes(status)} sx={{ p: 0.5, ml: 1 }} />
                <ListItemText primary={TASK_STATUS_LABELS[status]} />
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <TextField
            select
            label="דחיפות"
            value={value.priority}
            SelectProps={{
              multiple: true,
              renderValue: (selected) =>
                (selected as TaskPriority[]).map((p) => TASK_PRIORITY_LABELS[p]).join(', ') || 'הכל',
            }}
            onChange={(e) =>
              set(
                'priority',
                (typeof e.target.value === 'string' ? [] : e.target.value) as TaskPriority[]
              )
            }
          >
            {TASK_PRIORITIES.map((priority) => (
              <MenuItem key={priority} value={priority}>
                <Checkbox checked={value.priority.includes(priority)} sx={{ p: 0.5, ml: 1 }} />
                <ListItemText primary={TASK_PRIORITY_LABELS[priority]} />
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid size={{ xs: 12, md: 'auto' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={value.topLevelOnly}
                onChange={(e) => set('topLevelOnly', e.target.checked)}
              />
            }
            label="משימות אב בלבד"
          />
        </Grid>

        <Grid size={{ xs: 12, md: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              onClick={() => onChange({ ...EMPTY_FILTERS, ...pickLocked(value, locked) })}
              disabled={!hasActiveFilters(value)}
            >
              נקה סינון
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  )
}

const pickLocked = (
  value: TaskFilterState,
  locked: Array<keyof TaskFilterState>
): Partial<TaskFilterState> =>
  locked.reduce<Partial<TaskFilterState>>((acc, key) => ({ ...acc, [key]: value[key] }), {})
